import React, { FC, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, Select, CheckBox } from '../../../../../../components';
import { icons } from '../../../../../../icons';
import { HeadLine3 } from '../../../../../../components/typography/styled';
import { billingService } from '../../../../../../api';
import { projectService } from '../../../../../../api';
import { useUi } from '../../../../../../context/ui.context';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 20px;
  min-width: 400px;
  max-width: 500px;
  background: ${({ theme }) => theme.palette[10]};
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
`;

const HeadWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Icon = styled.img`
  width: 24px;
  height: 24px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
`;

const BodyModal = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 12px;
`;

interface CreateServiceModalProps {
  clientId: string | number;
  onSuccess: () => void;
  onCancel: () => void;
}

const CreateServiceModal: FC<CreateServiceModalProps> = ({
  clientId,
  onSuccess,
  onCancel,
}) => {
  const [t] = useTranslation();
  const { viewAlert } = useUi();
  const [products, setProducts] = useState<any[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<string>('');
  const [selectedCycleId, setSelectedCycleId] = useState<string>('');
  const [cycles, setCycles] = useState<any[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [createNewProject, setCreateNewProject] = useState<boolean>(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [serviceDataRes, projectsRes] = await Promise.all([
          billingService.getNewServiceData(clientId),
          projectService.getProjectList({
            page: 1,
            pageSize: 20,
            search: '',
            filtering: 'service:null',
          }),
        ]);
        const prods = serviceDataRes.data?.products || [];
        setProducts(prods);
        if (prods.length > 0) {
          setSelectedProductId(prods[0].id.toString());
          const firstCycles = prods[0].cycles || [];
          setCycles(firstCycles);
          if (firstCycles.length > 0) {
            setSelectedCycleId(firstCycles[0].id.toString());
          }
        }
        const projectList = projectsRes.data?.objects || projectsRes.data || [];
        setProjects(projectList);
      } catch (e: any) {
        viewAlert({
          severity: 'error',
          message: e?.response?.data?.detail || t('ERROR_OCCURRED'),
        });
      } finally {
        setDataLoading(false);
      }
    };
    fetchData();
  }, [clientId]);

  const handleProductChange = (value: string) => {
    setSelectedProductId(value);
    const product = products.find((p: any) => p.id.toString() === value);
    const newCycles = product?.cycles || [];
    setCycles(newCycles);
    if (newCycles.length > 0) {
      setSelectedCycleId(newCycles[0].id.toString());
    } else {
      setSelectedCycleId('');
    }
  };

  const handleCreate = async () => {
    if (!selectedProductId || !selectedCycleId) return;
    setLoading(true);
    try {
      const params: any = {
        client_id: clientId,
        product_id: Number(selectedProductId),
        product_cycle_id: Number(selectedCycleId),
        create_new_project: createNewProject,
      };
      if (!createNewProject && selectedProjectId) {
        params.project_id = selectedProjectId;
      }
      const res = await billingService.createOpenstackService(params);
      viewAlert({
        severity: 'success',
        message: res.data?.detail || t('SERVICE_CREATED_SUCCESSFULLY'),
      });
      onSuccess();
    } catch (e: any) {
      const errData = e?.response?.data;
      const message = errData?.non_field_errors?.[0]
        || errData?.detail
        || t('ERROR_OCCURRED');
      viewAlert({
        severity: 'error',
        message,
      });
    } finally {
      setLoading(false);
    }
  };

  const productOptions = products.map((p: any) => ({
    value: p.id.toString(),
    label: p.name,
  }));

  const cycleOptions = cycles.map((c: any) => ({
    value: c.id.toString(),
    label: c.display_name,
  }));

  const projectOptions = projects.map((p: any) => ({
    value: p.project_id || p.id,
    label: p.name || p.project_name || p.id,
  }));

  return (
    <Container>
      <HeadWrapper>
        <HeadLine3 style={{ marginLeft: '10px', marginTop: '10px' }}>
          {t('CREATE_OPENSTACK_SERVICE')}
        </HeadLine3>
        <Icon onClick={onCancel} src={icons.CloseIcon} />
      </HeadWrapper>
      <BodyModal>
        {dataLoading ? (
          <div>{t('LOADING')}...</div>
        ) : (
          <>
            <Select
              title={t('PRODUCT')}
              data={productOptions}
              selectedValue={selectedProductId}
              onChange={(e: any) => handleProductChange(e)}
              toolTip={false}
            />
            <Select
              title={t('BILLING_CYCLE')}
              data={cycleOptions}
              selectedValue={selectedCycleId}
              onChange={(e: any) => setSelectedCycleId(e)}
              toolTip={false}
            />
            <CheckBox
              value={createNewProject}
              onChange={() => setCreateNewProject(!createNewProject)}
              title={t('CREATE_NEW_PROJECT')}
            />
            {!createNewProject && (
              <Select
                title={t('PROJECT')}
                data={projectOptions}
                selectedValue={selectedProjectId}
                onChange={(e: any) => setSelectedProjectId(e)}
                toolTip={false}
              />
            )}
          </>
        )}
        <ButtonsWrapper>
          <Button
            variant='stroke'
            size='display'
            onClick={onCancel}
            disabled={loading}
            title={t('CANCEL')}
          />
          <Button
            variant='primary'
            size='display'
            onClick={handleCreate}
            loading={loading}
            disabled={!selectedProductId || !selectedCycleId || dataLoading}
            title={t('CREATE_OPENSTACK_SERVICE')}
          />
        </ButtonsWrapper>
      </BodyModal>
    </Container>
  );
};

export default CreateServiceModal;
